import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { Check } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/components/pin-setup-code-step";
import {
  messageAfterRefill,
  messageHint,
  shouldSaveMessage,
  type MessageFill,
  type MessageSaveStatus,
} from "@/lib/messageDraft";

// The account page's two reminder message boxes (Notifications tab: the SMS reminder
// message and the email message). The switches and dropdowns beside them save the
// moment they change; a box had no save of its own, so typed text was stored only
// when one of those happened to change. A box now saves when the admin leaves it.
// The rules are in client/src/lib/messageDraft.ts.

type MessageBox = {
  // The page's form for the card. Typed loosely: the two cards have different forms.
  form: UseFormReturn<any>;
  field: "customReminderMessage" | "customEmailMessage";
  /** The settings route that stores it: PATCH /api/accounts/:id/<route>. */
  route: "reminders" | "email-notifications";
};

export function useMessageDraft({ form, field, route }: MessageBox) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // What the box was last filled with. The ref is for decisions made outside a
  // render (a blur, leaving the page); the state copy redraws the line under the box.
  const filledWith = useRef<MessageFill>();
  const [fill, setFill] = useState<MessageFill>();
  const [status, setStatus] = useState<MessageSaveStatus>("idle");
  // The newest text handed to the server and not yet answered
  const onItsWay = useRef<MessageFill>();
  const saves = useRef<Promise<unknown>>(Promise.resolve());

  const fillWith = (next: MessageFill) => {
    const last = filledWith.current;
    filledWith.current = next;
    if (last?.accountId !== next.accountId || last.text !== next.text) setFill(next);
  };

  const send = async (message: MessageFill) => {
    const { accountId, text } = message;
    const stillThisAccount = () => filledWith.current?.accountId === accountId;
    try {
      // Only this field. The switches beside the box send the whole card, and a
      // whole-card save from here could land after one of them and undo it.
      await apiRequest("PATCH", `/api/accounts/${accountId}/${route}`, { [field]: text });
      if (stillThisAccount()) {
        fillWith({ accountId, text });
        if (onItsWay.current === message) setStatus("saved");
      }
      // One invalidation: this key is a prefix of the account's own queries, and a
      // second one for the account would cancel the first refetch and run it again
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
    } catch (error) {
      if (stillThisAccount() && onItsWay.current === message) setStatus("error");
      toast({
        title: "Message not saved",
        description: parseApiError(error).message ?? "Please try again.",
        variant: "destructive",
      });
    }
  };

  /** Leaving the box, or the page, saves what was typed — once, for the account it was typed on. */
  const save = () => {
    const typed: string = form.getValues(field) ?? "";
    const from = filledWith.current;
    if (!from || !shouldSaveMessage(typed, from, onItsWay.current)) return;
    const message = { accountId: from.accountId, text: typed };
    onItsWay.current = message;
    setStatus("saving");
    // One at a time, in the order typed: an older text can never land after a newer one
    saves.current = saves.current
      .then(() => send(message))
      .finally(() => {
        if (onItsWay.current === message) onItsWay.current = undefined;
      });
  };

  return {
    control: form.control,
    field,
    fill,
    status,
    save,
    /** For the page's effect that fills the form: what the box holds once the account arrives again. */
    refill: (accountId: number, serverText: string): string => {
      const text = messageAfterRefill(form.getValues(field), filledWith.current, accountId, serverText);
      if (filledWith.current?.accountId !== accountId) setStatus("idle");
      fillWith({ accountId, text: serverText });
      return text;
    },
    /** "Reset to org defaults" wins over text typed and not yet saved. */
    discard: (accountId: number, text: string) => {
      if (filledWith.current?.accountId !== accountId) return;
      form.setValue(field, text);
      fillWith({ accountId, text });
      setStatus("idle");
    },
    /** Settles once every save started so far has been answered. */
    settled: () => saves.current,
  };
}

/** The line under a message box: unsaved, saving, saved, or not saved with a way to try again. */
export function MessageSaveHint({ draft }: { draft: ReturnType<typeof useMessageDraft> }) {
  const typed = useWatch({ control: draft.control, name: draft.field });
  const hint = messageHint(typed, draft.fill, draft.status);
  const atRisk = hint === "unsaved" || hint === "saving" || hint === "failed";

  // Closing or reloading the tab from inside the box fires no blur, so nothing is
  // saved: have the browser ask before the text is thrown away.
  useEffect(() => {
    if (!atRisk) return;
    const ask = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [atRisk]);

  return (
    <p className="min-h-[1rem] text-xs text-muted-foreground" aria-live="polite" data-testid={`hint-${draft.field}`}>
      {hint === "unsaved" && "Unsaved. Saves when you leave this box."}
      {hint === "saving" && "Saving…"}
      {hint === "saved" && (
        <span className="inline-flex items-center gap-1 text-cn-brand-green">
          <Check className="h-3 w-3" />
          Saved
        </span>
      )}
      {hint === "failed" && (
        <span className="text-red-600">
          Not saved.{" "}
          <button type="button" className="underline" onClick={draft.save}>
            Try again
          </button>
        </span>
      )}
    </p>
  );
}
